import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'

export interface UseMapCenteringProps {
  centerOnRoute?: any
  centerOnSearch?: { latitude: number; longitude: number } | null
  centerOnUserLocation?: { latitude: number; longitude: number } | null
  centerOnWaypoint?: any
  centerOnWaypoints?: Array<{ latitude?: number | null; longitude?: number | null }> | null
  centerOnCandidate?: { latitude: number; longitude: number } | null
  centerOnPOI?: { latitude: number; longitude: number } | null
  centerOnDeadEnd?: { latitude: number; longitude: number } | null
}

/**
 * Hook for managing map centering behavior
 * Centers map on routes, waypoints, search results, or user location
 * Uses non-destructive centering to preserve layers
 */
export function useMapCentering({
  centerOnRoute,
  centerOnSearch,
  centerOnUserLocation,
  centerOnWaypoint,
  centerOnWaypoints,
  centerOnCandidate,
  centerOnPOI,
  centerOnDeadEnd
}: UseMapCenteringProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const hasCenteredOnUserRef = useRef(false)
  
  // Track previous centering targets to prevent unnecessary re-centering
  const previousCenterOnRouteIdRef = useRef<string | undefined>(undefined)
  const previousCenterOnSearchRef = useRef<{ latitude: number; longitude: number } | null>(null)
  const previousCenterOnWaypointIdRef = useRef<string | undefined>(undefined)
  const previousCenterOnWaypointsRef = useRef<string | undefined>(undefined)
  const previousCenterOnCandidateRef = useRef<{ latitude: number; longitude: number } | null>(null)
  const previousCenterOnPOIRef = useRef<{ latitude: number; longitude: number } | null>(null)
  const previousCenterOnDeadEndRef = useRef<{ latitude: number; longitude: number } | null>(null)

  useEffect(() => {
    // Reset previous route ID when centerOnRoute becomes null
    // This allows re-centering when the same route is selected again
    if (!centerOnRoute) {
      previousCenterOnRouteIdRef.current = undefined
      return
    }

    if (!map.current || !isLoaded || !styleLoaded) return

    const routeId = centerOnRoute.id || centerOnRoute.properties?.id
    if (!routeId) return

    const geometry = centerOnRoute.geometry || centerOnRoute.geojson || centerOnRoute.properties?.geometry
    if (!geometry) {
      // Geometry missing - reset previous ID to allow retry when geometry becomes available
      previousCenterOnRouteIdRef.current = undefined
      return
    }

    // Check if this is the same route AND geometry hasn't changed
    // We allow re-centering if the route ID changed or if geometry might have changed
    // To detect geometry changes, we check if the previous ID matches
    // If it matches, skip to prevent unnecessary re-centering
    // But we reset when centerOnRoute becomes null, allowing re-selection
    if (previousCenterOnRouteIdRef.current === routeId) {
      return
    }

    // Update previous ID after we've determined we should center
    previousCenterOnRouteIdRef.current = routeId

    const bounds = new mapboxgl.LngLatBounds()
    const extendBounds = (coords: any) => {
      if (Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
        coords.forEach((coord: any) => {
          if (Array.isArray(coord[0])) {
            extendBounds(coord)
            return
          }
          bounds.extend(coord as [number, number])
        })
      }
    }

    if (geometry.type === 'LineString') {
      extendBounds(geometry.coordinates)
    } else if (geometry.type === 'MultiLineString') {
      geometry.coordinates.forEach((line: any) => extendBounds(line))
    } else if (geometry.type === 'Point') {
      bounds.extend(geometry.coordinates as [number, number])
    }

    if (!bounds.isEmpty()) {
      const padding = { top: 160, bottom: 120, left: 120, right: 380 }
      map.current.fitBounds(bounds, {
        padding,
        duration: 850,
        essential: true
      })
    } else if (geometry.type === 'Point') {
      const [lng, lat] = geometry.coordinates as [number, number]
      map.current.flyTo({
        center: [lng, lat],
        zoom: Math.max(map.current.getZoom(), 12),
        duration: 650,
        essential: true
      })
    }
  }, [map, isLoaded, styleLoaded, centerOnRoute])

  // Center on search result - use flyTo with essential flag
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !centerOnSearch) return

    // Skip if same search location
    if (previousCenterOnSearchRef.current?.latitude === centerOnSearch.latitude &&
        previousCenterOnSearchRef.current?.longitude === centerOnSearch.longitude) {
      return
    }
    
    previousCenterOnSearchRef.current = centerOnSearch

    map.current.flyTo({
      center: [centerOnSearch.longitude, centerOnSearch.latitude],
      zoom: 14,
      duration: 800,
      essential: true // Essential animation preserves layers better
    })
  }, [map, isLoaded, styleLoaded, centerOnSearch])

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !centerOnWaypoint) return

    const waypointId = centerOnWaypoint.id || centerOnWaypoint.properties?.id
    if (!waypointId) return

    if (previousCenterOnWaypointIdRef.current === waypointId) return
    previousCenterOnWaypointIdRef.current = waypointId

    const latitude = centerOnWaypoint.latitude || centerOnWaypoint.properties?.latitude || centerOnWaypoint.geometry?.coordinates?.[1]
    const longitude = centerOnWaypoint.longitude || centerOnWaypoint.properties?.longitude || centerOnWaypoint.geometry?.coordinates?.[0]

    if (typeof latitude === 'number' && typeof longitude === 'number') {
      const currentZoom = map.current.getZoom()
      // Gentle zoom: only zoom in slightly if we're zoomed out too far, otherwise just center
      // Cap at zoom 12 to avoid aggressive zoom-in
      const targetZoom = Math.min(Math.max(currentZoom, 10), 12)
      map.current.flyTo({
        center: [longitude, latitude],
        zoom: targetZoom,
        duration: 600,
        essential: true
      })
    }
  }, [map, isLoaded, styleLoaded, centerOnWaypoint])

  // Center on multiple waypoints (e.g., when a trip is selected)
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !centerOnWaypoints || centerOnWaypoints.length === 0) {
      // Reset previous when centerOnWaypoints becomes null/empty
      if (!centerOnWaypoints || centerOnWaypoints.length === 0) {
        previousCenterOnWaypointsRef.current = undefined
      }
      return
    }

    // Filter waypoints with valid coordinates
    const validWaypoints = centerOnWaypoints.filter(
      wp => typeof wp.latitude === 'number' && typeof wp.longitude === 'number' &&
            !isNaN(wp.latitude) && !isNaN(wp.longitude)
    )

    if (validWaypoints.length === 0) return

    // Create a unique key from waypoint IDs/coordinates to detect changes
    const waypointsKey = validWaypoints
      .map(wp => `${wp.latitude},${wp.longitude}`)
      .sort()
      .join('|')

    // Skip if same waypoints (prevent unnecessary re-centering)
    if (previousCenterOnWaypointsRef.current === waypointsKey) return
    previousCenterOnWaypointsRef.current = waypointsKey

    // For single waypoint, use gentle zoom instead of fitBounds
    if (validWaypoints.length === 1) {
      const waypoint = validWaypoints[0]
      const currentZoom = map.current.getZoom()
      // Only zoom in slightly if we're zoomed out too far, otherwise just center
      const targetZoom = Math.min(Math.max(currentZoom, 10), 12)
      map.current.flyTo({
        center: [waypoint.longitude!, waypoint.latitude!],
        zoom: targetZoom,
        duration: 600,
        essential: true
      })
      return
    }

    // For multiple waypoints, use fitBounds
    const bounds = new mapboxgl.LngLatBounds()
    validWaypoints.forEach(wp => {
      bounds.extend([wp.longitude!, wp.latitude!])
    })

    if (!bounds.isEmpty()) {
      const padding = { top: 160, bottom: 120, left: 120, right: 380 }
      map.current.fitBounds(bounds, {
        padding,
        duration: 850,
        essential: true
      })
    }
  }, [map, isLoaded, styleLoaded, centerOnWaypoints])

  // Center on candidate (campsite discovery) - use flyTo with essential flag
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !centerOnCandidate) {
      // Reset previous when centerOnCandidate is null to allow re-centering
      if (!centerOnCandidate) {
        previousCenterOnCandidateRef.current = null
      }
      return
    }

    // Skip if same candidate location (but allow if previous was null)
    if (previousCenterOnCandidateRef.current &&
        previousCenterOnCandidateRef.current.latitude === centerOnCandidate.latitude &&
        previousCenterOnCandidateRef.current.longitude === centerOnCandidate.longitude) {
      return
    }
    
    previousCenterOnCandidateRef.current = centerOnCandidate

    map.current.flyTo({
      center: [centerOnCandidate.longitude, centerOnCandidate.latitude],
      zoom: 14,
      duration: 800,
      essential: true // Essential animation preserves layers better
    })
  }, [map, isLoaded, styleLoaded, centerOnCandidate])

  // Center on user location (only once, after style is fully loaded)
  // Uses jumpTo (no animation) to avoid style reload issues
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !centerOnUserLocation || hasCenteredOnUserRef.current) return

    // Don't center if there's another centering action active
    const hasOtherCentering = centerOnRoute || centerOnWaypoint || centerOnWaypoints || centerOnSearch || centerOnCandidate
    if (hasOtherCentering) return

    // Wait for map to be idle to ensure style is fully stable
    if (!map.current.loaded()) {
      map.current.once('idle', () => {
        if (map.current && !hasCenteredOnUserRef.current) {
          map.current.jumpTo({
            center: [centerOnUserLocation.longitude, centerOnUserLocation.latitude],
            zoom: 12
          })
          hasCenteredOnUserRef.current = true
        }
      })
      return
    }

    // Map is ready, center immediately with jumpTo (no animation)
    map.current.jumpTo({
      center: [centerOnUserLocation.longitude, centerOnUserLocation.latitude],
      zoom: 12
    })
    hasCenteredOnUserRef.current = true
  }, [map, isLoaded, styleLoaded, centerOnUserLocation, centerOnRoute, centerOnWaypoint, centerOnWaypoints, centerOnSearch, centerOnCandidate])

  // Center on POI (similar to waypoint centering)
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !centerOnPOI) return

    const poiKey = `${centerOnPOI.latitude},${centerOnPOI.longitude}`
    if (previousCenterOnPOIRef.current?.latitude === centerOnPOI.latitude && 
        previousCenterOnPOIRef.current?.longitude === centerOnPOI.longitude) {
      return
    }
    previousCenterOnPOIRef.current = centerOnPOI

    const latitude = centerOnPOI.latitude
    const longitude = centerOnPOI.longitude

    if (typeof latitude === 'number' && typeof longitude === 'number') {
      const currentZoom = map.current.getZoom()
      const targetZoom = Math.min(Math.max(currentZoom, 10), 12)
      map.current.flyTo({
        center: [longitude, latitude],
        zoom: targetZoom,
        duration: 600,
        essential: true
      })
    }
  }, [map, isLoaded, styleLoaded, centerOnPOI])

  // Center on Dead End (similar to POI centering)
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !centerOnDeadEnd) return

    if (previousCenterOnDeadEndRef.current?.latitude === centerOnDeadEnd.latitude && 
        previousCenterOnDeadEndRef.current?.longitude === centerOnDeadEnd.longitude) {
      return
    }
    previousCenterOnDeadEndRef.current = centerOnDeadEnd

    const latitude = centerOnDeadEnd.latitude
    const longitude = centerOnDeadEnd.longitude

    if (typeof latitude === 'number' && typeof longitude === 'number') {
      const currentZoom = map.current.getZoom()
      const targetZoom = Math.min(Math.max(currentZoom, 10), 12)
      map.current.flyTo({
        center: [longitude, latitude],
        zoom: targetZoom,
        duration: 600,
        essential: true
      })
    }
  }, [map, isLoaded, styleLoaded, centerOnDeadEnd])
}

